/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LE NIN — LA PART QUI NE TOUCHE PAS AU SECRET.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ POURQUOI CE FICHIER EXISTE (24/09/2026). `src/domaine/nin.ts` importe
 * `node:crypto` dès sa première ligne pour l'empreinte poivrée. Un écran qui
 * voudrait seulement VÉRIFIER un numéro devant l'usager ne peut donc pas
 * l'importer — et c'est exactement ce qui a fait du guichet un formulaire où le
 * NIN est un `<input>` nu : la règle était inatteignable depuis le navigateur.
 *
 * ⚠️ CE N'EST PAS UNE COPIE. `domaine/nin.ts` RÉ-EXPORTE ce fichier : il n'y a
 * toujours qu'une implémentation. Le poivre, lui, ne quitte pas le serveur.
 *
 */
const POIDS = [7, 3, 1] as const;
export const NIN_LONGUEUR = 15;

export const normaliserNin = (nin: string): string => nin.replace(/[\s\-.]/g, '');

function chiffres(nin: string): number[] | null {
  const net = normaliserNin(nin);
  if (net.length !== NIN_LONGUEUR || !/^\d+$/.test(net)) return null;
  return [...net].map(Number);
}

export function cleAttendue(corps: number[]): number {
  return corps.slice(0, 14).reduce((s, d, i) => s + d * POIDS[i % 3]!, 0) % 10;
}

export type MotifNinInvalide = 'format' | 'cle';

/**
 * ⚠️ Ne dit PAS que la personne existe — seulement que le numéro est cohérent.
 * Une clé modulo 10 attrape les fautes de frappe, pas les numéros inventés :
 * elle laisse passer environ un numéro au hasard sur dix.
 */
export function validerNin(nin: string): { valide: boolean; motif?: MotifNinInvalide } {
  const d = chiffres(nin);
  if (!d) return { valide: false, motif: 'format' };
  if (cleAttendue(d) !== d[14]) return { valide: false, motif: 'cle' };
  return { valide: true };
}

export interface NinDecode {
  sexe: 'homme' | 'femme' | null;
  neALEtranger: boolean;
  dateNaissance: string | null;
  siecleAmbigu: boolean;
  codeLieu: string;
  quatreDerniers: string;
}

/**
 * ⚠️ LA DATE DE NAISSANCE EST DANS LE NUMÉRO. La redemander après l'avoir
 * saisie, c'est faire ressaisir une donnée qu'on tient — et ouvrir la porte à
 * ce que les deux ne concordent pas.
 */
export function decoderNin(nin: string, aujourdhui = new Date()): NinDecode | null {
  const d = chiffres(nin);
  if (!d || cleAttendue(d) !== d[14]) return null;
  const n = (a: number, b: number) => Number(d.slice(a, b).join(''));

  const tete = d[0]!;
  const aa = n(1, 3), mois = n(3, 5), jour = n(5, 7);

  let dateNaissance: string | null = null;
  let siecleAmbigu = false;
  if (mois >= 1 && mois <= 12 && jour >= 1 && jour <= 31) {
    const an = aujourdhui.getFullYear();
    // Le siècle se tranche par plausibilité — majeur, moins de 120 ans. Quand
    // les deux lectures restent plausibles, ON NE TRANCHE PAS : une date
    // inventée voyagerait ensuite comme une donnée d'état civil.
    const candidats = [1900 + aa, 2000 + aa].filter((y) => an - y >= 18 && an - y < 120);
    if (candidats.length === 1) {
      const y = candidats[0]!;
      const dt = new Date(Date.UTC(y, mois - 1, jour));
      // Le 31 février n'existe pas : Date le décale en mars sans rien dire.
      dateNaissance = dt.getUTCMonth() === mois - 1 && dt.getUTCDate() === jour
        ? dt.toISOString().slice(0, 10) : null;
    } else if (candidats.length > 1) {
      siecleAmbigu = true;
    }
  }

  return {
    // `null` quand le chiffre de tête sort de la légende connue (1, 2, 3) :
    // une absence RÉELLE, pas une valeur à deviner.
    sexe: tete === 1 ? 'homme' : tete === 2 ? 'femme' : null,
    // Ne dit RIEN de la nationalité : c'est un Guinéen né hors du pays.
    neALEtranger: tete === 3,
    dateNaissance, siecleAmbigu,
    codeLieu: d.slice(7, 10).join(''),
    quatreDerniers: d.slice(11, 15).join(''),
  };
}


/** Affichage : quatre chiffres ne permettent pas de retrouver un numéro de quinze. */
export const masquerNin = (nin: string): string => {
  const net = normaliserNin(nin);
  return net.length === NIN_LONGUEUR ? `${'•'.repeat(11)}${net.slice(-4)}` : '•'.repeat(15);
};

/**
 * CHERCHE UN NIN DANS UNE STRUCTURE QUI VA ÊTRE PERSISTÉE.
 *
 * ⚠️⚠️ LE PIÈGE QU'ELLE FERME. `fon.mutation.effet_projete` est une colonne
 * JSONB : tout ce que l'appelant y met est ÉCRIT EN CLAIR dans la base, y reste
 * pour la vie du registre, et passe par `log_statement` et les traces de
 * requêtes lentes en chemin. Or l'effet d'une mutation DÉCLARE des personnes.
 * Un agent qui, de bonne foi, range le NIN d'un acquéreur dans
 * `referenceExterne` — ou dans un champ qu'il ajoute — met un numéro d'identité
 * national en clair dans un registre foncier. Tout le dispositif de ce dépôt
 * (empreinte poivrée, quatre derniers, numéro jamais en paramètre de requête)
 * serait contourné par une seule saisie, sans qu'aucune garde ne s'en aperçoive.
 *
 * ⚠️ ELLE MARCHE SUR L'OBJET BRUT, PAS SUR L'OBJET VALIDÉ. Zod retire les clés
 * inconnues à la lecture — mais c'est l'objet BRUT qui est stocké. Contrôler
 * après `parse()` ne verrait donc jamais le champ ajouté.
 *
 * ⚠️ ET LE FAUX POSITIF EST ASSUMÉ : une chaîne de quinze chiffres dont la clé
 * modulo 10 tombe juste est refusée, même si ce n'est pas un NIN (environ un
 * nombre de quinze chiffres sur dix). Le refus NOMME le champ et dit quoi
 * faire ; l'inverse — laisser passer un vrai NIN — ne se rattrape pas.
 */
export function chercherNinDans(valeur: unknown, chemin = ''): string | null {
  if (typeof valeur === 'string') {
    const net = normaliserNin(valeur);
    return /^\d{15}$/.test(net) && validerNin(net).valide ? (chemin || '(racine)') : null;
  }
  if (Array.isArray(valeur)) {
    for (let i = 0; i < valeur.length; i += 1) {
      const t = chercherNinDans(valeur[i], `${chemin}[${i}]`);
      if (t) return t;
    }
    return null;
  }
  if (valeur && typeof valeur === 'object') {
    for (const [cle, v] of Object.entries(valeur as Record<string, unknown>)) {
      const t = chercherNinDans(v, chemin ? `${chemin}.${cle}` : cle);
      if (t) return t;
    }
  }
  return null;
}
